import { Icon, IconVariant } from "../../Atoms/Icons/Icon/Icon";

import styles from "./IconButton.module.scss";

type IconButtonSize = "xs" | "sm" | "md" | "lg";
type IconButtonCategory = "primary" | "secondary" | "tertiary" | "status-icon";

interface IIconButtonProps {
    /**
     * The optional button ID.
     */
    id?: string;

    /**
     * The button color scheme.
     */
    variant?: IconVariant;

    /**
     * The category of importance.
     */
    category?: IconButtonCategory;

    /**
     * The size of the icon and button.
     */
    size?: IconButtonSize;

    /**
     * An ID to target in frontend tests.
     */
    dataCy?: string;

    /**
     * Whether to disable the button click event.
     */
    disabled?: boolean;

    /**
     * A callback to invoke when the button is clicked.
     */
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;

    /**
     * The icon that will be rendered inside the button
     */
    children: JSX.Element;
}

const variantMapper: Record<IconVariant, string> = {
    "brand": `${styles.iconButton} ${styles.brand}`,
    "error": `${styles.iconButton} ${styles.error}`,
    "neutral": `${styles.iconButton} ${styles.neutral}`,
    "success": `${styles.iconButton} ${styles.success}`,
    "warning": `${styles.iconButton} ${styles.warning}`
} as const;

const categoryMapper: Record<IconButtonCategory, string> = {
    "primary": styles.primary,
    "secondary": styles.secondary,
    "status-icon": styles.statusIcon,
    "tertiary": styles.tertiary
} as const;

/**
 * The icon button.
 * @param {IIconButtonProps} param The props used to render the icon button.
 * @returns The rendered component.
 */
function IconButton({
    id,
    variant = "brand",
    category = "primary",
    size = "md",
    children,
    dataCy,
    disabled,
    onClick
}: IIconButtonProps): JSX.Element {
    return (
        <button
            onClick={e => onClick?.(e)}
            disabled={disabled}
            data-cy={dataCy}
            id={id}
            className={`${variantMapper[variant]} ${categoryMapper[category]} ${styles[size]}`}
        >
            <Icon size={size}>
                {children}
            </Icon>
        </button>
    );
}

export { IconButton, IconButtonCategory, IconButtonSize, IIconButtonProps };
